/**
 * The camera side of Godot-editor viewport navigation, separated from the
 * gesture translation in `GodotEditorControls.tsx`: this holds the pose, the
 * gestures only edit it.
 */
import * as THREE from 'three';
import { editorCameraPosition } from './godotEditorCamera.js';
import { cursorCameraPosition, cursorFromCamera, cursorQuaternion, orthographicHeight, DEGENERATE_DISTANCE, } from './godotEditorCursor.js';
/**
 * three's own duck-typing flag rather than `instanceof`: a host app (or a test
 * runner) that ends up with two copies of three in the module graph still gets
 * the right answer, and the flag is what three itself checks internally.
 */
function isPerspectiveCamera(camera) {
    return 'isPerspectiveCamera' in camera && camera.isPerspectiveCamera;
}
/**
 * The orbit focus point and the editor camera, a perspective/orthographic pair. It
 * is R3F's `state.controls`: `frameSceneBounds` writes `target` and `camera.position`
 * and calls `update()`. No pose is cached, so a gesture picks up an external write.
 * Extends `THREE.EventDispatcher` because R3F's `state.controls` slot takes that type.
 */
export class EditorControlsHandle extends THREE.EventDispatcher {
    setActiveCamera;
    /** The orbit focus point. One persistent vector: `frameSceneBounds` copies into it. */
    target = new THREE.Vector3();
    /** Written only by the projection swap: one of the pair below. */
    editorCamera;
    perspectiveCamera;
    orthographicCamera = null;
    aspect = 1;
    constructor(camera, 
    /** Publishes a projection swap to R3F, which is what actually renders it. */
    setActiveCamera) {
        super();
        this.setActiveCamera = setActiveCamera;
        if (!isPerspectiveCamera(camera)) {
            throw new Error(`expected a perspective editor camera, got ${camera.type}`);
        }
        this.editorCamera = camera;
        this.perspectiveCamera = camera;
    }
    /**
     * The editor camera, never an authored Camera3D: Godot keeps its editor camera
     * apart from the one it previews (`node_3d_editor_plugin.cpp:4486-4502`).
     */
    get camera() {
        return this.editorCamera;
    }
    /** Whether R3F draws through a previewed Camera3D instead of the editor camera. */
    isPreviewing(rendered) {
        return rendered !== this.editorCamera;
    }
    setAspect(aspect) {
        if (!Number.isFinite(aspect) || aspect <= 0)
            return;
        this.aspect = aspect;
        this.applyProjection(this.cursor());
    }
    /** The live cursor, always re-derived from the camera and the focus point. */
    cursor() {
        return cursorFromCamera(this.camera.position, this.target);
    }
    /** The perspective fov, which also sizes the orthographic frustum through `orthographicHeight`. */
    fovDegrees() {
        return this.perspectiveCamera.fov;
    }
    /** The zoom range Godot derives from the camera's clip planes. */
    zoomRange() {
        return { near: this.camera.near, far: this.camera.far };
    }
    /** Move the camera to a cursor: the only place a gesture's result lands. */
    applyCursor(cursor) {
        this.target.copy(cursor.target);
        if (cursor.distance > DEGENERATE_DISTANCE) {
            this.camera.position.copy(cursorCameraPosition(cursor));
            this.camera.quaternion.copy(cursorQuaternion(cursor));
        }
        this.applyProjection(cursor);
        this.camera.updateMatrixWorld();
    }
    /**
     * R3F's controls contract, and the second half of `frameSceneBounds`: point the
     * camera at the target. It only re-orients, since repositioning would undo the
     * framing that just wrote `camera.position`.
     */
    update() {
        const cursor = this.cursor();
        if (cursor.distance > DEGENERATE_DISTANCE) {
            this.camera.quaternion.copy(cursorQuaternion(cursor));
        }
        this.applyProjection(cursor);
        this.camera.updateMatrixWorld();
    }
    /** Back to the pose Godot's editor opens every scene at, in perspective. */
    reset() {
        this.usePerspective();
        this.target.set(0, 0, 0);
        this.camera.position.set(...editorCameraPosition());
        this.update();
    }
    get isOrthographic() {
        return this.camera === this.orthographicCamera;
    }
    /** Numpad 5. */
    toggleProjection() {
        if (this.isOrthographic)
            this.usePerspective();
        else
            this.useOrthographic();
    }
    usePerspective() {
        if (this.editorCamera === this.perspectiveCamera)
            return;
        const cursor = this.cursor();
        this.editorCamera = this.perspectiveCamera;
        this.setActiveCamera(this.perspectiveCamera);
        this.applyCursor(cursor);
    }
    useOrthographic() {
        const perspective = this.perspectiveCamera;
        const cursor = this.cursor();
        if (!this.orthographicCamera) {
            // R3F resizes an orthographic camera into PIXEL units unless the camera
            // claims its own projection; Godot's ortho frustum is world-sized and
            // derived from the orbit radius, so `manual` opts out of that.
            const ortho = new THREE.OrthographicCamera(-1, 1, 1, -1);
            ortho.manual = true;
            this.orthographicCamera = ortho;
        }
        this.orthographicCamera.near = perspective.near;
        this.orthographicCamera.far = perspective.far;
        this.editorCamera = this.orthographicCamera;
        this.setActiveCamera(this.orthographicCamera);
        this.applyCursor(cursor);
    }
    /**
     * Orthographic has no perspective divide, so pulling the eye back changes
     * nothing on screen; Godot keeps zoom working by sizing the frustum to what
     * the perspective camera would see AT the focus point.
     */
    applyProjection(cursor) {
        const ortho = this.orthographicCamera;
        if (!ortho || this.editorCamera !== ortho)
            return;
        const height = orthographicHeight(cursor.distance, this.fovDegrees());
        const width = height * this.aspect;
        ortho.left = -width / 2;
        ortho.right = width / 2;
        ortho.top = height / 2;
        ortho.bottom = -height / 2;
        ortho.updateProjectionMatrix();
    }
}
/**
 * Whether the canvas draws through a previewed Camera3D instead of its editor camera. A canvas
 * with no editor controls has no preview to draw.
 */
export function isDrawingPreview(state) {
    return state.controls instanceof EditorControlsHandle && state.controls.isPreviewing(state.camera);
}
//# sourceMappingURL=EditorControlsHandle.js.map